import api from '@/lib/api';
import type { MetricsStatus, RunnerPlatform } from '@/api/runners';

export type ExternalRunnerStatus = 'WAITING_FOR_INSTALL' | 'ONLINE' | 'OFFLINE' | 'REVOKED';

export type ReinstallReason = 'CONFIG_CHANGED' | 'AGENT_OUTDATED' | 'CREDENTIAL_REVOKED';

/** Specifications from the server's newest sample. */
export interface ExternalRunnerSpecs {
  /** Online hardware threads. */
  cpuLogical: number | null;
  /** Physical cores; null when the server's CPU topology is unreadable. */
  cpuCores: number | null;
  memoryTotal: number | null;
  diskTotal: number | null;
  reportedAt: string;
}

/** A self-hosted runner server that reports metrics but is not created or controlled by Manage Runners. */
export interface ExternalRunnerResponse {
  runnerId: string;
  name: string;
  platform: RunnerPlatform;
  labels: string[];
  created: string;
  monitoringEnabled: boolean;
  metricsConnectionId?: string;
  metricsConnectionName?: string;
  metricsStatus?: { status: MetricsStatus; reportedAt: string } | null;
  status: ExternalRunnerStatus;
  lastSeenAt: string | null;
  installedAt: string | null;
  agentVersion: string | null;
  currentAgentVersion: string;
  reinstallNeeded: boolean;
  reinstallReasons: ReinstallReason[];
  specs: ExternalRunnerSpecs | null;
}

/** Omitted fields keep their value on update; an empty metricsConnectionId switches Metrics off. */
export interface ExternalRunnerRequest {
  name?: string;
  platform?: RunnerPlatform;
  labels?: string[];
  monitoringEnabled?: boolean;
  metricsConnectionId?: string;
}

export interface InstallLink {
  url: string;
  command: string;
  expiresAt: string;
  uninstallCommand: string;
}

export async function listExternalRunners() {
  const { data } = await api.get<ExternalRunnerResponse[]>('external-runner');
  return data;
}

export async function getExternalRunner(runnerId: string) {
  const { data } = await api.get<ExternalRunnerResponse>(`external-runner/${runnerId}`);
  return data;
}

export async function createExternalRunner(request: ExternalRunnerRequest) {
  const { data } = await api.post<ExternalRunnerResponse>('external-runner', request);
  return data;
}

export async function updateExternalRunner(runnerId: string, request: ExternalRunnerRequest) {
  const { data } = await api.put<ExternalRunnerResponse>(`external-runner/${runnerId}`, request);
  return data;
}

export async function deleteExternalRunner(runnerId: string) {
  const { data } = await api.delete<{ runnerId: string; uninstallCommand: string }>(
    `external-runner/${runnerId}`
  );
  return data;
}

/** A single-use install command, valid for 15 minutes; it replaces the server's credential once used. */
export async function createInstallLink(runnerId: string) {
  const { data } = await api.post<InstallLink>(`external-runner/${runnerId}/install-link`);
  return data;
}
